import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import type { useDrawingPersistence } from '../../hooks/useDrawingPersistence';
import { useActiveTenant } from '../../tenancy/activeTenant';
import { PublishDrawingDialog } from './PublishDrawingDialog';
import { publicDrawingPath, publicReactionPath } from '../../features/public/publicAccess';

type Persistence = ReturnType<typeof useDrawingPersistence>;

interface PersistencePanelProps {
  readonly persistence: Persistence;
  readonly onSave: () => void;
  readonly onNewDrawing: () => void;
}

export function PersistencePanel({ persistence, onSave, onNewDrawing }: PersistencePanelProps) {
  const { t } = useTranslation();
  const {
    remoteDrawing,
    hasUnsavedChanges,
    drawingTitle,
    updateDrawingTitle,
    persistence: persistenceState,
    changeArchiveStatus,
    changeVisibility,
    publishPrompt,
    answerPublishPrompt
  } = persistence;
  const busy = persistenceState.state === 'loading' || persistenceState.state === 'saving';
  // #1051 (P6): a drawing saves back to its own tenant -- say which when it isn't the active one,
  // and keep Save off where the user's role is view-only.
  const tenant = useActiveTenant();
  const openTenantId = persistence.openTenantId;
  const readOnly = tenant.roleIn(openTenantId) === 'VIEWER';
  const elsewhere = openTenantId !== undefined && openTenantId !== (tenant.active?.id ?? null);
  const elsewhereNote = elsewhere
    ? openTenantId === null
      ? t('tenancy.savedInPersonal')
      : t('tenancy.savedIn', { tenant: tenant.nameOf(openTenantId) ?? t('tenancy.page.organisation') })
    : null;
  const isArchived = remoteDrawing?.archivedAt != null;
  // What the Public switch acts on: the open drawing, or the open reaction (#1124).
  const shared = remoteDrawing ?? persistence.remoteReaction;
  const archiveLabel = isArchived ? t('persistencePanel.restore') : t('persistencePanel.archive');

  // #705: a saved (or opened) reaction saves over itself too.
  let saveLabel =
    remoteDrawing || persistence.remoteReaction
      ? t('persistencePanel.saveChanges')
      : t('persistencePanel.createAndSave');
  if (persistenceState.state === 'saving') saveLabel = t('persistencePanel.saving');

  const statusLine = remoteDrawing
    ? t('persistencePanel.statusLine', {
        // #882: named by its title, never the raw ID (owner: users don't need it).
        title: remoteDrawing.savedTitle || t('persistencePanel.untitledDrawing'),
        version: remoteDrawing.documentRevision,
        archived: isArchived ? t('persistencePanel.archivedSuffix') : '',
        unsaved: hasUnsavedChanges ? t('persistencePanel.unsavedSuffix') : t('persistencePanel.savedSuffix')
      })
    : t('persistencePanel.newDrawing');

  return (
    <section className="persistence-panel" aria-label={t('persistencePanel.regionLabel')}>
      {/* #882 (owner): no heading -- the File drawer's "Save" group label already names this. */}
      {/* #882: the status line is the save/open confirmation (no duplicate message below), so it's
          announced when it changes. */}
      <p role="status">
        {statusLine}
        {elsewhereNote ? ` · ${elsewhereNote}` : ''}
      </p>
      {/* #837 (owner): no "load by ID" -- drawings open from My drawings and search results. #882
          (owner): nor is the ID shown; users don't need it. */}
      <label className="drawing-title-editor">
        {/* #882 (owner): "Title" is the placeholder; the label stays for screen readers only. */}
        <span className="visually-hidden">{t('persistencePanel.titleLabel')}</span>
        <input
          type="text"
          value={drawingTitle}
          maxLength={200}
          placeholder={t('persistencePanel.titleLabel')}
          onChange={(event) => updateDrawingTitle(event.target.value)}
        />
      </label>
      <div className="persistence-actions">
        <button
          type="button"
          className="icon-button"
          onClick={onSave}
          disabled={busy || readOnly || (remoteDrawing !== null && !hasUnsavedChanges)}
          aria-label={saveLabel}
          title={readOnly ? t('tenancy.viewOnly') : saveLabel}
        >
          <Icon name={remoteDrawing ? 'save' : 'saveAs'} />
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={onNewDrawing}
          disabled={busy}
          aria-label={t('persistencePanel.newDrawingButton')}
          title={t('persistencePanel.newDrawingHint')}
        >
          <Icon name="noteAdd" />
        </button>
        {remoteDrawing ? (
          <button
            type="button"
            className="icon-button"
            onClick={() => void changeArchiveStatus(remoteDrawing.id, remoteDrawing.documentRevision, !isArchived)}
            disabled={busy}
            aria-label={archiveLabel}
            title={archiveLabel}
          >
            <Icon name={isArchived ? 'unarchive' : 'archive'} />
          </button>
        ) : null}
        {shared ? (
          // #811 -- private by default; public lets others find it in search, view it and add it to
          // their own paper (only you can change it). #1124: a saved reaction too, at /r/<id>.
          <button
            type="button"
            className={`icon-button${shared.visibility === 'public' ? ' active' : ''}`}
            data-testid="drawing-visibility"
            onClick={() => void changeVisibility(shared.id, shared.visibility === 'public' ? 'private' : 'public')}
            disabled={busy}
            aria-pressed={shared.visibility === 'public'}
            aria-label={t('persistencePanel.public')}
            title={t(shared.visibility === 'public' ? 'persistencePanel.publicOn' : 'persistencePanel.publicOff')}
          >
            <Icon name={shared.visibility === 'public' ? 'public' : 'lock'} />
          </button>
        ) : null}
        {shared?.visibility === 'public' ? (
          // #1141 (owner) -- a public drawing or reaction's page, one click away: no id to look up.
          <a
            className="icon-button"
            data-testid="public-page-link"
            href={remoteDrawing ? publicDrawingPath(shared.id) : publicReactionPath(shared.id)}
            target="_blank"
            rel="noopener"
            aria-label={t('persistencePanel.openPublicPage')}
            title={t('persistencePanel.openPublicPage')}
          >
            <Icon name="openInNew" />
          </a>
        ) : null}
      </div>
      {publishPrompt ? (
        <PublishDrawingDialog privateMolecules={publishPrompt.privateMolecules} onAnswer={answerPublishPrompt} />
      ) : null}
      {persistenceState.state === 'error' ? (
        <p className="import-error" role="alert">
          {persistenceState.message}
        </p>
      ) : null}
      {persistenceState.state === 'success' ? (
        <p className="import-success" role="status">
          {persistenceState.message}
        </p>
      ) : null}
    </section>
  );
}
